import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../ui/sonner';
import { messageErreur } from '../../lib/erreurs';
import { lotsAPI, nouvelleCleLot } from '../../lib/lotsApi';
import { jourDuJour } from '../../lib/lotsFiltres';

export const ACTION_DEPART_RETOUR = 'START';
export const ACTION_RENDU = 'DONE';

const TITRES = {
  [ACTION_DEPART_RETOUR]: 'Départ retour',
  [ACTION_RENDU]: 'Rendu',
};

/**
 * « Départ retour » ou « Rendu » pour les camions cochés, à la date choisie
 * (aujourd'hui par défaut). Le serveur rend ce qui a changé, ce qui l'était
 * déjà et les camions refusés avec leur phrase (`onDone`).
 */
export const RetourDialog = ({ open, onOpenChange, action = ACTION_DEPART_RETOUR, lineIds = [], onDone }) => {
  const [jour, setJour] = useState(jourDuJour());
  const [note, setNote] = useState('');
  const [enCours, setEnCours] = useState(false);
  const cle = useRef(null);

  useEffect(() => {
    if (!open) return;
    setJour(jourDuJour());
    setNote('');
    setEnCours(false);
    cle.current = nouvelleCleLot('lot-retour');
  }, [open, action]);

  const valider = async (evenement) => {
    evenement.preventDefault();
    if (enCours || lineIds.length === 0) return;
    setEnCours(true);
    try {
      const envoyer = action === ACTION_RENDU ? lotsAPI.finishReturns : lotsAPI.startReturns;
      const resultat = await envoyer({ lineIds, at: jour, note: note.trim(), idempotencyKey: cle.current });
      cle.current = null;
      onDone?.(resultat, action);
      onOpenChange(false);
    } catch (err) {
      toast.error(messageErreur(err, "Le retour n'a pas pu être enregistré"));
    } finally {
      setEnCours(false);
    }
  };

  const titre = TITRES[action] || TITRES[ACTION_DEPART_RETOUR];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="lot-retour-dialog">
        <DialogHeader>
          <DialogTitle>
            {titre} · {lineIds.length} camion{lineIds.length > 1 ? 's' : ''}
          </DialogTitle>
          <DialogDescription className="sr-only">Choisir la date du retour des camions cochés.</DialogDescription>
        </DialogHeader>
        <form onSubmit={valider} className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="retour-date">{action === ACTION_RENDU ? 'Date de remise' : 'Date de départ'}</Label>
            <Input
              id="retour-date"
              type="date"
              value={jour}
              max={jourDuJour()}
              onChange={(evenement) => setJour(evenement.target.value)}
              className="input-base"
              data-testid="retour-date"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="retour-note">Note</Label>
            <Input
              id="retour-note"
              value={note}
              onChange={(evenement) => setNote(evenement.target.value)}
              className="input-base"
              data-testid="retour-note"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={enCours}>
              Annuler
            </Button>
            <Button type="submit" disabled={enCours || lineIds.length === 0 || !jour} data-testid="retour-valider">
              {enCours ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              {titre}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RetourDialog;
